import React, { useState, useEffect, useContext, useRef } from "react";
import RenderProcess from "./renderProcess";
import ListProcess from "./ListProcess";
import Form from "./Form/Form";
import { ENDPOINT, isAdmin } from "./config";
import { Callout } from "@dev-spendesk/grapes";
import { AuthenticatedApiContext } from "../context/AuthenticatedApiContext";
import AdminPanel from "./AdminPanel/AdminPanel";
import { Layout } from "./Layout";
import { navigate, paths, useRoute } from "./router";

const VIEWS = {
  LIST: "list",
  PROCESS: "process",
  CREATE: "create",
  ADMIN: "admin",
};

export default function SalesPlatform({ userLogged }) {
  const route = useRoute();
  const currentView = route.name;
  const selectedProcessId = route.params.processId;

  const [processes, setProcesses] = useState([]);
  const [feedback, setFeedback] = useState({});
  const [isFeedbackVisible, setIsFeedbackVisible] = useState(false);
  const [isFetchingProcesses, setIsFetchingProcesses] = useState(true);

  const { get, del, post } = useContext(AuthenticatedApiContext);

  useEffect(() => {
    fetchProcesses();
  }, []);

  const previousView = useRef(currentView);
  useEffect(() => {
    if (previousView.current !== currentView && currentView === VIEWS.LIST) {
      fetchProcesses();
    }
    previousView.current = currentView;
  }, [currentView]);

  useEffect(() => {
    if (feedback.type) {
      setIsFeedbackVisible(true);
      const timer = setTimeout(() => {
        setIsFeedbackVisible(false);
        setTimeout(() => setFeedback({}), 300);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback.type, feedback.message]);

  // === API Functions ===

  async function fetchProcesses() {
    setIsFetchingProcesses(true);
    try {
      const results = await get(`${ENDPOINT}/processes`);
      if (results.length) {
        setProcesses(results);
      }
    } catch (err) {
      console.log(err);
      setFeedback({
        type: "alert",
        message: "Couldn't fetch data from server. Contact the support team.",
      });
    } finally {
      setIsFetchingProcesses(false);
    }
  }

  async function createCompany(data) {
    try {
      const company = await post(`${ENDPOINT}/create-company`, data);
      if (company.status === 400 || company.error) {
        setFeedback({
          type: "alert",
          message: `Failed to create company. Reason: ${company.error}`,
        });
        return;
      }
      navigateToProcess(company.process_id);
      await fetchProcesses();
    } catch (err) {
      setFeedback({
        type: "alert",
        message:
          "Failed to create company. Please try again or contact support.",
      });
    }
  }

  async function deleteProcess(processId) {
    try {
      await del(`${ENDPOINT}/processes/${processId}`);
      await fetchProcesses();
      setFeedback({
        type: "success",
        message: "Your company has been successfully deleted",
      });
      navigateToList();
    } catch (err) {
      console.log(err);
      navigateToList();
    }
  }

  // === Navigation Functions ===

  function navigateToList() {
    navigate(paths.list());
  }

  function navigateToProcess(processId) {
    navigate(paths.process(processId));
  }

  function navigateToCreate() {
    navigate(paths.create());
  }

  function navigateToAdmin() {
    navigate(paths.admin());
  }

  // === Render ===

  const renderCurrentView = () => {
    switch (currentView) {
      case VIEWS.PROCESS:
        return (
          <RenderProcess
            processId={selectedProcessId}
            key={selectedProcessId}
            isProcessSelected={navigateToList}
            deleteProcess={deleteProcess}
            setFeedback={setFeedback}
          />
        );

      case VIEWS.CREATE:
        return (
          <Form
            onSubmit={createCompany}
            userEmail={userLogged?.email}
            onHomepageClick={navigateToList}
            processes={processes}
          />
        );

      case VIEWS.ADMIN:
        return (
          <AdminPanel
            onHomepageClick={navigateToList}
            setFeedback={setFeedback}
            post={post}
          />
        );

      case VIEWS.LIST:
      default:
        return (
          <ListProcess
            processes={processes}
            onProcessSelection={navigateToProcess}
            userEmail={userLogged?.email}
            onCreateCompanyClick={navigateToCreate}
            deleteProcess={deleteProcess}
            isLoading={isFetchingProcesses}
          />
        );
    }
  };

  return (
    <Layout
      onAdminClick={navigateToAdmin}
      currentView={currentView}
      showAdminButton={isAdmin(userLogged?.email)}
    >
      {feedback.type && (
        <div
          style={{
            position: "fixed",
            top: "70px",
            left: "50%",
            zIndex: 100,
            width: "50%",
            opacity: isFeedbackVisible ? 1 : 0,
            transform: isFeedbackVisible
              ? "translateX(-50%) translateY(0)"
              : "translateX(-50%) translateY(-20px)",
            transition: "all 0.3s ease-in-out",
          }}
        >
          <Callout title={feedback.message} variant={feedback.type} />
        </div>
      )}

      {renderCurrentView()}
    </Layout>
  );
}
